// Framework-independent session transport shared by SPAs and landings.
// Browser storage is origin-local AND site/API scoped. Legacy unscoped state
// is never imported: the security cutover requires a fresh sign-in.
export function scopedStorage(storage, api, site, purpose = "session") {
  const prefix = "multisaas:v1:" + encodeURIComponent(api.replace(/\/$/, "")) + ":" +
    encodeURIComponent(site || "default") + ":" + purpose + ":";
  return {
    getItem(key) { return storage.getItem(prefix + key); },
    setItem(key, value) { storage.setItem(prefix + key, value); },
    removeItem(key) { storage.removeItem(prefix + key); },
    keys() {
      const found = [];
      try {
        for (let i = 0; i < storage.length; i++) {
          const raw = storage.key(i);
          if (raw && raw.indexOf(prefix) === 0) found.push(raw.slice(prefix.length));
        }
      } catch (_) { /* blocked storage: nothing to enumerate */ }
      return found;
    }
  };
}

export function readJson(storage, key, fallback = null) {
  try { return JSON.parse(storage.getItem(key) || "null") || fallback; }
  catch (_) { storage.removeItem(key); return fallback; }
}

export async function authRequest(api, site, path, body, token, fetcher = fetch) {
  const headers = { "Content-Type": "application/json" };
  if (site) headers["X-Site-Slug"] = site;
  if (token) headers.Authorization = "Bearer " + token;
  const response = await fetcher(api.replace(/\/$/, "") + path, {
    method: "POST", headers, body: JSON.stringify(body)
  });
  if (!response.ok) {
    const error = new Error("Session request failed (" + response.status + ")");
    error.status = response.status; // No request config or secret-bearing payload in errors.
    throw error;
  }
  return response.json();
}

export async function resumeGuest(api, site, storage, fetcher = fetch) {
  const token = storage.getItem("token");
  try {
    const result = await authRequest(api, site, "/auth/anonymous",
      token ? { guest_token: token } : {}, null, fetcher);
    storage.setItem("token", result.access_token);
    return result;
  } catch (error) {
    // Never silently create a new identity after a failed possession check.
    if (error.status === 401) {
      storage.removeItem("token");
      storage.setItem("lost", "1");
    }
    throw error;
  }
}

export async function createHandoff(api, site, token, purpose = "session", resourceId = null, fetcher = fetch) {
  const result = await authRequest(api, site, "/auth/guest-handoffs",
    { purpose, resource_id: resourceId }, token, fetcher);
  const path = result.continuation && result.continuation.path;
  if (!path || path[0] !== "/" || path.startsWith("//")) throw new Error("Invalid destination");
  return result.destination + path + "#handoff=" + encodeURIComponent(result.code) +
    "&purpose=" + encodeURIComponent(purpose);
}

export function takeHandoff(location, history) {
  const params = new URLSearchParams((location.hash || "").replace(/^#/, ""));
  if (!params.has("handoff") && !params.has("token")) return null;
  // Scrub before any request, including on rejected legacy handoffs.
  history.replaceState(history.state, "", location.pathname + location.search);
  if (params.has("token")) throw new Error("This old session link is no longer valid. Return to the original page.");
  return { code: params.get("handoff"), purpose: params.get("purpose") || "session" };
}

export function exchangeHandoff(api, site, incoming, fetcher = fetch) {
  return authRequest(api, site, "/auth/guest-handoffs/exchange", incoming, null, fetcher);
}

export function handoffDisposition(user, primaryToken) {
  // An unknown existing identity is protected too; validate it before adoption.
  return primaryToken && (!user || !user.is_anonymous) ? "secondary" : "primary";
}

export function canAdoptHandoff(startingToken, currentToken, currentUser) {
  return startingToken === currentToken && handoffDisposition(currentUser, currentToken) === "primary";
}

export async function refreshCurrentSession(storage, refresh, accept) {
  const original = storage.getItem("token");
  if (!original) throw new Error("No session to refresh");
  try {
    const next = await refresh(original);
    const current = storage.getItem("token");
    if (current !== original) {
      if (current) return current;
      throw new Error("Session ended while refreshing");
    }
    accept(next);
    return next;
  } catch (error) {
    const current = storage.getItem("token");
    // A late response from a revoked guest must not overwrite an upgrade.
    if (current && current !== original) return current;
    if (current === original) storage.removeItem("token");
    throw error;
  }
}
